/**
 * Roadmap Service - Supabase CRUD operations for shared roadmaps
 * 
 * Architecture:
 * - Roadmaps table: Stores all unique roadmaps universally (generated once, shared by all)
 * - User_roadmaps table: Junction table tracking which roadmaps each user has viewed/generated
 * - Users only see roadmaps they've interacted with, but roadmaps are shared universally
 * 
 * NOW WITH: LocalStorage caching for offline access and 7-day auto-clearing
 */

import { supabase } from '@/config/supabase';
import { generateRoadmapId } from '@/utils/idGenerator';
import { normalizeLanguage } from '@/config/languages';
import { CacheService } from './cacheService';
import type { RoadmapData } from '@/types';

export interface RoadmapRecord {
  id: string;
  user_id?: string;
  title: string;
  goal: string;
  months: any;
  completed_days: any;
  language: string;
  intensity: string;
  meta?: any;
  view_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * Save or update a roadmap (upsert)
 * If roadmap with same ID exists, it returns existing one
 */
export async function saveRoadmap(
  roadmap: RoadmapData,
  userId?: string
): Promise<{ data: RoadmapRecord | null; error: any; isNew: boolean }> {
  try {
    // Normalize language to canonical format
    const { code: langCode, name: langName } = normalizeLanguage(roadmap.language || roadmap.lang || 'en');
    
    const goal = roadmap.goal || roadmap.meta?.goal || roadmap.title;
    const intensity = roadmap.intensity || 'Beginner';
    
    // Generate roadmap ID: goal_intensity_language
    const id = generateRoadmapId(
      goal,
      intensity,
      langCode // Use language code, not name
    );

    // Check if roadmap already exists
    const { data: existing, error: existError } = await supabase
      .from('roadmaps')
      .select('*')
      .eq('id', id);

    if (existing && existing.length > 0) {
      // Roadmap already exists, just return it
      return { data: existing[0], error: null, isNew: false };
    }

    // Create new roadmap
    const record: Partial<RoadmapRecord> = {
      id,
      user_id: userId,
      title: roadmap.title,
      goal: goal,
      months: roadmap.months || {},
      completed_days: roadmap.completedDays || {},
      language: langCode,  // Store canonical language code
      intensity: intensity,
      meta: roadmap.meta ? {
        ...roadmap.meta,
        language: langName,      // Store language name in meta
        languageCode: langCode,  // Store language code in meta for lookups
      } : {
        language: langName,
        languageCode: langCode,
      },
      view_count: 0,
    };

    const { data, error } = await supabase
      .from('roadmaps')
      .insert([record])
      .select();

    if (data && data.length > 0) {
      // Cache roadmap to localStorage for offline access
      const cacheKey = CacheService.generateKey('roadmap', data[0].id);
      CacheService.set(cacheKey, data[0], 1000 * 60 * 60 * 24 * 7, 'roadmap');
      console.log('✅ Roadmap cached to localStorage');
    }

    return { data: data && data.length > 0 ? data[0] : null, error, isNew: true };
  } catch (error) {
    console.error('Error saving roadmap:', error);
    return { data: null, error, isNew: false };
  }
}

/**
 * Get a roadmap by its structured ID
 */
export async function getRoadmapById(
  id: string
): Promise<{ data: RoadmapRecord | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('roadmaps')
      .select('*')
      .eq('id', id);

    const record = data && data.length > 0 ? data[0] : null;

    // Increment view count
    if (record && !error) {
      await supabase
        .from('roadmaps')
        .update({ view_count: (record.view_count || 0) + 1 })
        .eq('id', id);
    }

    return { data: record, error };
  } catch (error) {
    return { data: null, error };
  }
}

/**
 * Search roadmaps by goal, language, or intensity
 */
export async function searchRoadmaps(filters: {
  goal?: string;
  language?: string;
  intensity?: string;
  limit?: number;
}): Promise<{ data: RoadmapRecord[] | null; error: any }> {
  try {
    let query = supabase
      .from('roadmaps')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters.goal) {
      query = query.ilike('goal', `%${filters.goal}%`);
    }
    if (filters.language) {
      query = query.eq('language', filters.language);
    }
    if (filters.intensity) {
      query = query.eq('intensity', filters.intensity);
    }
    if (filters.limit) {
      query = query.limit(filters.limit);
    }

    const { data, error } = await query;
    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
}

/**
 * Get all roadmaps (with optional pagination)
 */
export async function getAllRoadmaps(
  limit: number = 50,
  offset: number = 0
): Promise<{ data: RoadmapRecord[] | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('roadmaps')
      .select('*')
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
}

/**
 * Remove a roadmap from user's dashboard (unlink from user_roadmaps)
 * Does NOT delete the roadmap from the database (roadmaps table)
 * Roadmaps are shared - this only removes it from one user's view
 * Supports both UUID (Supabase Auth) and email (fallback) identification
 */
export async function unlinkRoadmapFromUser(
  userIdOrEmail: string,
  roadmapId: string
): Promise<{ error: any }> {
  try {
    // Try UUID first (Supabase Auth)
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userIdOrEmail);
    
    let query = supabase
      .from('user_roadmaps')
      .delete()
      .eq('roadmap_id', roadmapId);

    if (isUUID) {
      query = query.eq('auth_user_id', userIdOrEmail);
    } else {
      query = query.eq('email', userIdOrEmail);
    }

    const { error } = await query;
    return { error };
  } catch (error) {
    return { error };
  }
}

/**
 * Delete a roadmap completely (admin only - removes from database)
 * CAUTION: This removes the roadmap for ALL users
 * Only call this for cleanup/maintenance
 */
export async function deleteRoadmapPermanently(
  id: string
): Promise<{ error: any }> {
  try {
    const { error } = await supabase
      .from('roadmaps')
      .delete()
      .eq('id', id);

    return { error };
  } catch (error) {
    return { error };
  }
}

/**
 * Convert RoadmapRecord to RoadmapData format for app compatibility
 */
export function recordToRoadmapData(record: RoadmapRecord): RoadmapData {
  // Ensure completedDays is always an array (Supabase might return it as object or null)
  let completedDays: string[] = [];
  if (Array.isArray(record.completed_days)) {
    completedDays = record.completed_days;
  } else if (record.completed_days && typeof record.completed_days === 'object') {
    // If it's an object like { "0": "day1", "1": "day2" }, convert to array
    completedDays = Object.values(record.completed_days);
  }
  
  return {
    id: record.id,
    title: record.title,
    goal: record.goal,
    months: record.months || [],
    completedDays: completedDays,
    language: record.language,
    lang: record.language, // For backward compatibility
    intensity: record.intensity,
    meta: record.meta || {
      goal: record.goal,
      duration: '',
      studyTime: ''
    },
    created_at: record.created_at,
  };
}

/**
 * Link roadmap to user (tracks user interaction)
 * Creates entry in user_roadmaps junction table
 * Called after roadmap is generated or viewed
 * Supports both UUID (Supabase Auth) and email (fallback) identification
 */
export const linkRoadmapToUser = async (
  userIdOrEmail: string,
  roadmapId: string,
  actionType: 'created' | 'viewed' = 'viewed'
) => {
  try {
    // Detect if input is UUID or email
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userIdOrEmail);
    
    const upsertData: any = {
      roadmap_id: roadmapId,
      action_type: actionType,
      updated_at: new Date().toISOString()
    };

    if (isUUID) {
      upsertData.auth_user_id = userIdOrEmail;
    } else {
      upsertData.email = userIdOrEmail;
    }

    // Upsert: Update if exists, insert if not
    const { data, error } = await supabase
      .from('user_roadmaps')
      .upsert(upsertData, {
        onConflict: isUUID ? 'auth_user_id,roadmap_id' : 'email,roadmap_id'
      })
      .select();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error linking roadmap to user:', error);
    return { data: null, error: error instanceof Error ? error.message : 'Failed to link roadmap' };
  }
};

/**
 * Get roadmaps for specific user (only show user's own roadmaps)
 * Returns only roadmaps the user has viewed or generated
 * Supports both UUID (Supabase Auth) and email (fallback) identification
 */
export const getUserRoadmaps = async (userIdOrEmail: string, limit = 10, offset = 0) => {
  try {
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userIdOrEmail);
    console.log('🔍 Fetching roadmaps for user:', userIdOrEmail, isUUID ? '(UUID)' : '(email)');
    
    // First, get the user_roadmaps records
    let userRoadmapsQuery = supabase
      .from('user_roadmaps')
      .select('roadmap_id');

    if (isUUID) {
      userRoadmapsQuery = userRoadmapsQuery.eq('auth_user_id', userIdOrEmail);
    } else {
      userRoadmapsQuery = userRoadmapsQuery.eq('email', userIdOrEmail);
    }

    const { data: userRoadmapRecords, error: userRoadmapsError } = await userRoadmapsQuery
      .order('updated_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (userRoadmapsError) {
      console.warn('⚠️ Supabase query error (user_roadmaps):', userRoadmapsError);
      throw userRoadmapsError;
    }

    if (!userRoadmapRecords || userRoadmapRecords.length === 0) {
      console.log('ℹ️ No roadmaps found for user');
      return { data: [], error: null };
    }

    // Extract roadmap IDs
    const roadmapIds = userRoadmapRecords.map(r => r.roadmap_id);
    console.log('📋 Found roadmap IDs:', roadmapIds);

    // Then, fetch the actual roadmap data
    const { data: roadmapData, error: roadmapsError } = await supabase
      .from('roadmaps')
      .select('*')
      .in('id', roadmapIds);

    if (roadmapsError) {
      console.warn('⚠️ Supabase query error (roadmaps):', roadmapsError);
      throw roadmapsError;
    }

    console.log('📦 Raw Supabase response:', roadmapData);
    
    // Convert roadmap records to RoadmapData
    const roadmaps = (roadmapData || [])
      .map((r: any) => {
        const converted = recordToRoadmapData(r);
        console.log('✅ Converted roadmap:', { id: converted.id, title: converted.title });
        return converted;
      });
    
    console.log(`✅ Retrieved ${roadmaps.length} roadmap(s) for user`);
    return { data: roadmaps, error: null };
  } catch (error) {
    console.error('❌ Error fetching user roadmaps:', error);
    return { data: [], error: error instanceof Error ? error.message : 'Failed to fetch roadmaps' };
  }
};

/**
 * Check if roadmap exists in universal database
 * Used to prevent re-generation of existing roadmaps
 */
export const checkRoadmapExists = async (roadmapId: string) => {
  try {
    const { data, error } = await supabase
      .from('roadmaps')
      .select('id')
      .eq('id', roadmapId)
      .maybeSingle(); // Returns null instead of error when no rows found

    if (error) throw error; // Only actual database errors, not "no rows"
    return { exists: !!data, error: null };
  } catch (error) {
    console.error('Error checking roadmap existence:', error);
    return { exists: false, error: error instanceof Error ? error.message : 'Failed to check roadmap' };
  }
};
/**
 * Get all completed article IDs for a user in a specific roadmap
 * This checks user_article_progress table for completed articles
 * Used to mark days as completed in RoadmapViewer
 */
/**
 * DEBUG: Check what's actually in the user_article_progress table for this user
 */
export const debugGetUserArticleProgress = async (userEmail: string) => {
  try {
    const { data, error } = await supabase
      .from('user_article_progress')
      .select('*')
      .eq('email', userEmail);
    
    console.group('🔍 DEBUG: User Article Progress Records');
    console.log('User Email:', userEmail);
    console.log('Records found:', data?.length || 0);
    console.log('Records:', data);
    console.groupEnd();
    
    return data || [];
  } catch (error) {
    console.error('Error fetching progress records:', error);
    return [];
  }
};

export const getCompletedArticlesForRoadmap = async (userEmail: string, roadmapId: string) => {
  try {
    console.group('📚 Loading Completed Articles for Roadmap');
    console.log('Roadmap ID:', roadmapId);
    console.log('User Email:', userEmail);
    
    // Query user_article_progress directly for this roadmap
    // Don't filter by articles table since we saved dayId (not article table ID)
    const { data: progress, error: progressError } = await supabase
      .from('user_article_progress')
      .select('article_id, status, progress_percentage')
      .eq('email', userEmail)
      .eq('roadmap_id', roadmapId)
      .or('status.eq.completed,progress_percentage.eq.100'); // Either completed OR 100%

    if (progressError) {
      console.error('❌ Error fetching progress:', progressError);
      throw progressError;
    }

    console.log('📊 User progress records:', progress);
    
    const completedIds = (progress || []).map(p => p.article_id);
    console.log(`✅ Found ${completedIds.length} completed articles`);
    console.log('Completed article IDs:', completedIds);
    console.groupEnd();
    
    return { completedArticleIds: completedIds, error: null };
  } catch (error) {
    console.error('❌ Error getting completed articles:', error);
    console.groupEnd();
    return { completedArticleIds: [], error };
  }
};